import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, BookOpen, Calendar, LayoutGrid, List, Building2, Loader2, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultStatusId } from '@/hooks/useDefaultStatus';
import PriceBookFormDialog, { PriceBookFormData } from '@/components/pricebook/PriceBookFormDialog';
import PriceBookItemImportWizard, { PriceBookItemImportRow } from '@/components/pricebook/PriceBookItemImportWizard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { priceBookService, priceBookItemService, profileService, externalSystemService, type ServiceContext } from '@/services';
import type { PriceBookListView } from '@/services';

interface ExternalSystem {
  id: string;
  name: string;
}

export default function PriceBooks() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExternalSystem, setSelectedExternalSystem] = useState<string | null>(
    searchParams.get('external_system')
  );
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [newPriceBookId, setNewPriceBookId] = useState<string | null>(null);

  // Fetch profile to get company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: priceBooks = [], isLoading } = useQuery<PriceBookListView[]>({
    queryKey: ['price-books', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await priceBookService.listForCards(profile.company_id);
      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!profile?.company_id,
  });

  const handleSaveNewPriceBook = async (data: PriceBookFormData): Promise<string> => {
    if (!profile?.company_id || !user?.id) {
      toast.error('Company not found');
      throw new Error('Company not found');
    }

    // Get default status_id for new price books
    const defaultStatusId = await getDefaultStatusId('price_book');

    const result = await priceBookService.create(
      {
        name: data.name,
        vendor_id: data.vendor_id,
        region_id: data.region_id,
        segment_id: data.segment_id,
        status_id: defaultStatusId,
        effective_date: data.effective_date,
        expiry_date: data.expiry_date,
      },
      { companyId: profile.company_id, userId: user.id }
    );

    if (result.error || !result.data) {
      toast.error('Failed to create price book');
      throw result.error || new Error('Failed to create price book');
    }

    toast.success('Price book created successfully');
    queryClient.invalidateQueries({ queryKey: ['price-books'] });
    setNewPriceBookId(result.data.id);
    setNewDialogOpen(false);
    setImportWizardOpen(true);
    return result.data.id;
  };

  const handleSaveAndClose = async (data: PriceBookFormData): Promise<string> => {
    if (!profile?.company_id || !user?.id) {
      toast.error('Company not found');
      throw new Error('Company not found');
    }

    // Get default status_id for new price books
    const defaultStatusId = await getDefaultStatusId('price_book');

    const result = await priceBookService.create(
      {
        name: data.name,
        vendor_id: data.vendor_id,
        region_id: data.region_id,
        segment_id: data.segment_id,
        status_id: defaultStatusId,
        effective_date: data.effective_date,
        expiry_date: data.expiry_date,
      },
      { companyId: profile.company_id, userId: user.id }
    );

    if (result.error || !result.data) {
      toast.error('Failed to create price book');
      throw result.error || new Error('Failed to create price book');
    }

    toast.success('Price book created successfully');
    queryClient.invalidateQueries({ queryKey: ['price-books'] });
    return result.data.id;
  };

  const handleImportItems = async (items: PriceBookItemImportRow[]) => {
    if (!newPriceBookId || !profile?.company_id || !user?.id) return;

    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
    let maxSequence = 0;
    const itemsToInsert = items.map((item) => ({
      price_book_id: newPriceBookId,
      sequence: ++maxSequence,
      itemcode: item.itemcode,
      description: item.description,
      price: item.price ?? 0,
      unit: item.unit ?? null,
      discount: item.discount ?? 0,
      supplier_itemcode: item.supplier_itemcode ?? null,
      supplier_description: item.supplier_description ?? null,
      is_active: item.is_active ?? true,
      locked_when_used: item.locked_when_used ?? true,
    }));

    const result = await priceBookItemService.createBatch(itemsToInsert, context);
    if (result.error) throw result.error;

    toast.success(`Imported ${items.length} items`);
    queryClient.invalidateQueries({ queryKey: ['price-books'] });
    navigate(`/price-books/${newPriceBookId}`);
  };

  // Fetch external systems for filter
  const { data: externalSystems = [] } = useQuery({
    queryKey: ['external-systems', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await externalSystemService.list(profile.company_id);
      if (result.error) throw result.error;
      return result.data as ExternalSystem[];
    },
    enabled: !!profile?.company_id,
  });

  // Sync external system filter with URL
  useEffect(() => {
    if (selectedExternalSystem) {
      setSearchParams({ external_system: selectedExternalSystem });
    } else {
      setSearchParams({});
    }
  }, [selectedExternalSystem, setSearchParams]);

  const filteredBooks = priceBooks.filter(book => {
    const matchesSearch = book.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesExternalSystem = !selectedExternalSystem || book.external_system === selectedExternalSystem;
    
    return matchesSearch && matchesExternalSystem;
  });

  const hasActiveFilters = searchQuery || selectedExternalSystem;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedExternalSystem(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Price Books</h1>
          <p className="page-subtitle">Create and manage pricing catalogs for invoice validation.</p>
        </div>
        <button className="btn-primary" onClick={() => setNewDialogOpen(true)}>
          <Plus className="w-5 h-5" />
          New Price Book
        </button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center justify-between">
        <div className="flex flex-col md:flex-row gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search price books..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-12"
            />
          </div>

          {/* External System Filter */}
          {externalSystems.length > 0 && (
            <Select
              value={selectedExternalSystem || "all"}
              onValueChange={(value) => setSelectedExternalSystem(value === "all" ? null : value)}
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All External Systems" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All External Systems</SelectItem>
                {externalSystems.map((system) => (
                  <SelectItem key={system.id} value={system.name}>
                    {system.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="w-full md:w-auto"
            >
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(value) => value && setViewMode(value as 'cards' | 'table')}
          className="bg-muted rounded-lg p-1"
        >
          <ToggleGroupItem value="cards" aria-label="Card view" className="px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm">
            <LayoutGrid className="w-4 h-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="table" aria-label="Table view" className="px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm">
            <List className="w-4 h-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBooks.map((book) => (
            <div key={book.id} className="stat-card cursor-pointer group" onClick={() => navigate(`/price-books/${book.id}`)}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <BookOpen className="w-6 h-6 text-primary group-hover:text-primary-foreground" />
                </div>
                <Badge variant="secondary">
                  {book.status_name || '—'}
                </Badge>
              </div>
              <h3 className="font-semibold text-foreground mb-1">{book.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span>{book.vendor_name || 'No vendor'}</span>
              </div>
              {(book.region_name || book.segment_name) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  {book.region_name && <span>{book.region_name}</span>}
                  {book.region_name && book.segment_name && <span>•</span>}
                  {book.segment_name && <span>{book.segment_name}</span>}
                </div>
              )}
              {!book.region_name && !book.segment_name && <div className="mb-3" />}
              <div className="flex flex-col gap-1 text-sm text-muted-foreground border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <span>Effective:</span>
                  <span>{formatDate(book.effective_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Expires:</span>
                  <span>{formatDate(book.expiry_date)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span>Items:</span>
                  <span>{book.item_count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Vendor</th>
                <th>Region</th>
                <th>Effective Date</th>
                <th>Expiry Date</th>
                <th>Items</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBooks.map((book) => (
                <tr key={book.id} className="cursor-pointer" onClick={() => navigate(`/price-books/${book.id}`)}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <span className="font-medium">{book.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      {book.vendor_name || 'No vendor'}
                    </div>
                  </td>
                  <td className="text-muted-foreground">
                    {book.region_name || '—'}
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      {formatDate(book.effective_date)}
                    </div>
                  </td>
                  <td>{formatDate(book.expiry_date)}</td>
                  <td>{book.item_count}</td>
                  <td>
                    <Badge variant="secondary">
                      {book.status_name || '—'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredBooks.length === 0 && !isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          {priceBooks.length === 0 
            ? 'No price books yet. Create your first price book to get started.'
            : 'No price books found matching your search.'}
        </div>
      )}

      <PriceBookFormDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        priceBook={null}
        onSave={handleSaveNewPriceBook}
        saveButtonText="Save & Import Items"
        onSecondarySave={handleSaveAndClose}
        secondarySaveButtonText="Save & Close"
      />

      <PriceBookItemImportWizard
        open={importWizardOpen}
        onOpenChange={setImportWizardOpen}
        onImport={handleImportItems}
      />
    </div>
  );
}
